import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { BarChart3, Building, Calendar, Copy, Pencil, Target, Users } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Competition } from '@/types/competitions';
import { CATEGORY_ICONS } from '@/lib/constants/habitBadges';
import { competitionDayCount } from '@/lib/competitions/competitionDates';
import { competitionCounts, type CompetitionCounts } from '@/lib/competitions/competitionDetails';
import { competitionInsights, type CompetitionInsights } from '@/lib/competitions/competitionInsights';
import { loadCompetitionStandings, standingUserName } from '@/lib/firebase/queries/competitionStandings';
import { CompetitionInsightsPanel } from '@/components/shared/CompetitionInsightsPanel';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface CompetitionDetailsDialogProps {
  competition: Competition;
  organizationName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  statusLabel: string;
  statusClassName: string;
  published: boolean;
  publishToggling: boolean;
  onTogglePublish: () => void;
  onEdit: () => void;
  onShowResults: () => void;
  onCopy: () => void;
}

/**
 * Read-only view of one competition (namat#26): its dates, habits,
 * organization, groups and counts. Editing and publishing stay one tap away.
 */
export function CompetitionDetailsDialog({
  competition,
  organizationName,
  open,
  onOpenChange,
  statusLabel,
  statusClassName,
  published,
  publishToggling,
  onTogglePublish,
  onEdit,
  onShowResults,
  onCopy,
}: CompetitionDetailsDialogProps) {
  const { t, dir, language } = useLanguage();
  const [counts, setCounts] = useState<CompetitionCounts | null>(null);
  const [insights, setInsights] = useState<CompetitionInsights | null>(null);
  const [countsFailed, setCountsFailed] = useState(false);
  const align = dir === 'rtl' ? 'text-right' : 'text-left';

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCounts(null);
    setInsights(null);
    setCountsFailed(false);
    // Same standings as the results dialog: counts follow the current dates.
    // The insights (namat#35) reuse the same logs and accounts: no extra read.
    loadCompetitionStandings(competition.id, competition.organizationId, '', competition)
      .then(standings => {
        if (cancelled) return;
        setCounts(competitionCounts(standings));
        const unknown = t('dashboard.manager.competitions.insights.unknown') || 'Unknown';
        const names = new Map(Array.from(standings.participantUsers.entries()).map(([id, u]) => [id, standingUserName(u, language) || unknown]));
        setInsights(competitionInsights({
          logs: standings.logs,
          habits: competition.habits,
          participantIds: standings.individuals.map(p => p.userId),
          names,
          window: competition,
          now: new Date(),
        }));
      })
      .catch(error => {
        console.error('Error loading competition counts:', error);
        if (!cancelled) setCountsFailed(true);
      });
    return () => { cancelled = true; };
  }, [open, competition, language]);

  const days = competitionDayCount(competition);
  const label = (key: string, fallback: string) => t(`dashboard.manager.competitions.details.${key}`) || fallback;
  const stat = (value: number | undefined, text: string) => (
    <div className="rounded-lg border p-3 text-center">
      {value === undefined ? (
        countsFailed ? <div className="text-2xl font-semibold">—</div> : <Skeleton className="h-8 w-12 mx-auto" />
      ) : (
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
      )}
      <div className="text-xs text-muted-foreground mt-1">{text}</div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" dir={dir}>
        <DialogHeader>
          <DialogTitle className={align}>{competition.name}</DialogTitle>
          <DialogDescription className={align} asChild>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={statusClassName}>{statusLabel}</Badge>
              <Badge
                variant="outline"
                className={published ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : 'border-amber-300 text-amber-700 bg-amber-50'}
              >
                {published
                  ? (t('dashboard.manager.competitions.published') || 'Published')
                  : (t('dashboard.manager.competitions.unpublished') || 'Unpublished')}
              </Badge>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {competition.description && (
            <p className={`text-sm whitespace-pre-wrap ${align}`}>{competition.description}</p>
          )}

          <div className="grid gap-3 sm:grid-cols-2 text-sm">
            <div className="flex items-start gap-2">
              <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <div className="text-muted-foreground">{label('dates', 'Dates')}</div>
                <div className="font-medium">
                  {format(competition.startDate, 'MMM dd, yyyy')} – {format(competition.endDate, 'MMM dd, yyyy')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {label('days', '{count} days').replace('{count}', String(days))}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Building className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <div className="text-muted-foreground">{label('organization', 'Organization')}</div>
                <div className="font-medium">{organizationName || competition.organizationId}</div>
              </div>
            </div>
          </div>

          <div>
            <h4 className={`flex items-center gap-2 text-sm font-semibold mb-2 ${align}`}>
              <Users className="h-4 w-4" />
              {label('counts', 'Participation')}
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {stat(competition.habits.length, label('habitsCount', 'Habits'))}
              {stat(counts?.participants, label('participants', 'Participants'))}
              {stat(counts?.activeParticipants, label('activeParticipants', 'Logged at least once'))}
              {stat(counts?.completions, label('completions', 'Completed habits'))}
              {stat(counts?.groups, label('groups', 'Groups'))}
              {stat(counts?.sections, label('sections', 'Sections'))}
            </div>
          </div>

          <CompetitionInsightsPanel insights={insights} failed={countsFailed} habitCount={competition.habits.length} />

          <div>
            <h4 className={`flex items-center gap-2 text-sm font-semibold mb-2 ${align}`}>
              <Target className="h-4 w-4" />
              {t('dashboard.manager.competitions.habits') || 'Competition Habits'}
            </h4>
            <ul className="space-y-2">
              {competition.habits.map(habit => (
                <li key={habit.id} className="flex items-start gap-3 rounded-lg border p-3">
                  <Badge variant="secondary">
                    {CATEGORY_ICONS[habit.category as keyof typeof CATEGORY_ICONS]} {t(`habits.categories.${habit.category.toLowerCase()}`) || habit.category}
                  </Badge>
                  <div className="min-w-0">
                    <div className="font-medium">{habit.name}</div>
                    {habit.description && <div className="text-sm text-muted-foreground">{habit.description}</div>}
                    {habit.stepsTarget && (
                      <div className="text-xs text-muted-foreground">
                        {(t('dashboard.manager.competitions.habitStepsTarget') || 'Steps target')}: {habit.stepsTarget}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3 text-sm">
            <span>{t('dashboard.manager.competitions.isPublishedLabel') || 'Visible to beneficiaries'}</span>
            <Switch
              checked={published}
              disabled={publishToggling}
              onCheckedChange={onTogglePublish}
              aria-label={
                published
                  ? (t('dashboard.manager.competitions.unpublish') || 'Unpublish')
                  : (t('dashboard.manager.competitions.publish') || 'Publish')
              }
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={onCopy}>
            <Copy className="h-4 w-4 mr-2" />
            {t('dashboard.manager.competitions.copy.title') || 'Copy competition'}
          </Button>
          <Button type="button" variant="outline" onClick={onShowResults}>
            <BarChart3 className="h-4 w-4 mr-2" />
            {t('dashboard.manager.competitions.showResults') || 'Show Results'}
          </Button>
          <Button type="button" onClick={onEdit}>
            <Pencil className="h-4 w-4 mr-2" />
            {t('dashboard.manager.competitions.editCompetition') || 'Edit Competition'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
